import React, { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import i18n from '../i18n';
import {
  hasPermission as checkPermission,
  getAccessibleModules as accessibleModulesFor,
} from '../utils/permissions';
import { startSessionKeepAlive, isTokenExpired, markSessionExpired } from '../utils/sessionRefresh';
import { isNativeApp, onAppResume } from '../utils/native';
import { enrollDevice, ensureFreshSession, forgetDevice } from '../utils/deviceSession';
import { unregisterPhone } from '../utils/mobileLine';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Sentry user context — captured errors get attached to a user, tenant, role.
// PII-safe: id + tenant + role only, no email/name.
function syncSentryUser(userData) {
  if (!userData) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: userData.id || userData._id,
    username: userData.username,  // technical handle, not real name
  });
  Sentry.setTag('tenant_id', userData.tenant_id || 'none');
  Sentry.setTag('role', userData.role || 'unknown');
}

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Exposed so pages doing `const { token } = useAuth()` for raw fetch() calls
  // send a real Bearer token. Read lazily from localStorage so it is already
  // set on the first render (pages fire their fetches from mount effects).
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem('token');
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  // Keep in sync when another tab logs in/out (or the axios 401 handler
  // clears storage in this one and a later storage event fires).
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === null || e.key === 'token') {
        setToken(localStorage.getItem('token'));
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Sliding session: keep the token fresh while the user is active.
  useEffect(() => {
    if (!token) return undefined;
    return startSessionKeepAlive(setToken);
  }, [token]);

  const restoreStoredSession = () => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (token && userData) {
      try {
        const parsed = JSON.parse(userData);
        setUser(parsed);
        setToken(token);
        syncSentryUser(parsed);
        // Set token in axios default headers (redundant with interceptor but safe)
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
      }
    }
  };

  useEffect(() => {
    if (!isNativeApp()) {
      // OFF-24: an expired stored token went straight to a burst of 401s;
      // drop it and let the login page say the session ended.
      const stored = localStorage.getItem('token');
      if (stored && isTokenExpired(stored)) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        markSessionExpired();
      }
      restoreStoredSession();
      setLoading(false);
      return undefined;
    }
    // Mobile app: renew an expired session with the device key first, so
    // opening the app days later lands on the dashboard, not on /login.
    let cancelled = false;
    ensureFreshSession()
      .catch(() => null)
      .finally(() => {
        if (cancelled) return;
        restoreStoredSession();
        setLoading(false);
      });
    // Back from the background: the JWT may have expired meanwhile.
    const stopResume = onAppResume(() => {
      ensureFreshSession().then((fresh) => {
        if (fresh) restoreStoredSession();
      }).catch(() => {});
    });
    return () => {
      cancelled = true;
      stopResume();
    };
  }, []);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        username,
        password
      });
      
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setToken(access_token);
      setUser(userData);
      syncSentryUser(userData);
      // Mobile app: keep this login for good (see utils/deviceSession).
      enrollDevice(access_token);
      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || i18n.t('shared.auth.loginFailed')
      };
    }
  };

  const logout = () => {
    // Mobile app: stop ringing this phone, then drop its device key.
    unregisterPhone(localStorage.getItem('token'));
    forgetDevice();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    syncSentryUser(null);
  };

  // Permission checks mirror the backend (see utils/permissions.js): only
  // role admin/superuser bypasses; everyone else needs position permissions.
  // Usage: hasPermission('inventory', 'read') or hasPermission('inventory.products', 'create')
  const hasPermission = (...args) => checkPermission(user, ...args);

  // Get accessible modules for current user
  const getAccessibleModules = () => accessibleModulesFor(user);

  return (
    <AuthContext.Provider value={{ 
      user,
      token,
      login,
      logout, 
      loading,
      hasPermission,
      getAccessibleModules
    }}>
      {children}
    </AuthContext.Provider>
  );
};